import React, { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Loader2, Bell, Mail, Smartphone, MessageSquare, Save, Check } from "lucide-react";

interface NotificationPreference {
  notification_type: string;
  email_enabled: boolean;
  in_app_enabled: boolean;
  sms_enabled: boolean;
}

interface NotificationTypeConfig {
  type: string;
  label: string;
  description: string;
}

const NOTIFICATION_TYPES: NotificationTypeConfig[] = [
  {
    type: "team_invite",
    label: "Team Invitations",
    description: "When someone invites you to join a team",
  },
  {
    type: "team_invite_create",
    label: "Create Team Invitations",
    description: "When someone invites you to create and lead a new team",
  },
  {
    type: "invite_accepted",
    label: "Invitation Accepted",
    description: "When someone accepts your invitation",
  },
  {
    type: "added_to_team",
    label: "Added to Team",
    description: "When you're added to a team you wanted to join",
  },
  {
    type: "challenge_updates",
    label: "Challenge Updates",
    description: "Updates about challenges you've joined",
  },
  {
    type: "event_reminders",
    label: "Event Reminders",
    description: "Reminders about upcoming events",
  },
  {
    type: "marketing",
    label: "Marketing & Promotions",
    description: "News, tips, and promotional content",
  },
];

const NotificationSettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [preferences, setPreferences] = useState<Record<string, NotificationPreference>>({});
  const [userId, setUserId] = useState<string | null>(null);

  // Fetch user and preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoading(false);
          return;
        }
        setUserId(user.id);

        const { data, error } = await supabase
          .from("user_notification_preferences")
          .select("*")
          .eq("user_id", user.id);

        if (error) throw error;

        // Convert array to record keyed by notification_type
        const prefsMap: Record<string, NotificationPreference> = {};
        NOTIFICATION_TYPES.forEach((config) => {
          const existing = data?.find((p) => p.notification_type === config.type);
          prefsMap[config.type] = existing || {
            notification_type: config.type,
            email_enabled: true,
            in_app_enabled: true,
            sms_enabled: false,
          };
        });
        setPreferences(prefsMap);
      } catch (error) {
        console.error("Error fetching preferences:", error);
        toast.error("Failed to load notification preferences");
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, []);

  const handleToggle = async (
    notificationType: string,
    channel: "email_enabled" | "in_app_enabled" | "sms_enabled",
    value: boolean
  ) => {
    if (!userId) return;

    // Optimistically update UI
    setPreferences((prev) => ({
      ...prev,
      [notificationType]: {
        ...prev[notificationType],
        [channel]: value,
      },
    }));

    try {
      // Upsert the preference
      const { error } = await supabase
        .from("user_notification_preferences")
        .upsert({
          user_id: userId,
          notification_type: notificationType,
          email_enabled: channel === "email_enabled" ? value : preferences[notificationType]?.email_enabled ?? true,
          in_app_enabled: channel === "in_app_enabled" ? value : preferences[notificationType]?.in_app_enabled ?? true,
          sms_enabled: channel === "sms_enabled" ? value : preferences[notificationType]?.sms_enabled ?? false,
          updated_at: new Date().toISOString(),
        }, {
          onConflict: "user_id,notification_type",
        });

      if (error) throw error;

      // Show brief save success indicator
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 1500);
    } catch (error: any) {
      console.error("Error saving preference:", error);
      toast.error("Failed to save preference");
      
      // Revert optimistic update
      setPreferences((prev) => ({
        ...prev,
        [notificationType]: {
          ...prev[notificationType],
          [channel]: !value,
        },
      }));
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="h-6 w-6" />
            Notification Settings
          </h1>
          <p className="text-muted-foreground mt-1">
            Control how and when you receive notifications
          </p>
        </div>
        {saveSuccess && (
          <div className="flex items-center gap-2 text-green-600 bg-green-50 px-3 py-1.5 rounded-full text-sm">
            <Check className="h-4 w-4" />
            Saved
          </div>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Notification Channels</CardTitle>
          <CardDescription>
            Choose how you'd like to be notified for each type of update
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Channel Headers */}
          <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 pb-4 border-b mb-4">
            <div className="text-sm font-medium text-muted-foreground">Notification Type</div>
            <div className="flex items-center gap-1 text-sm font-medium text-muted-foreground min-w-[60px] justify-center">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Email</span>
            </div>
            <div className="flex items-center gap-1 text-sm font-medium text-muted-foreground min-w-[60px] justify-center">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">In-App</span>
            </div>
            <div className="flex items-center gap-1 text-sm font-medium text-muted-foreground min-w-[60px] justify-center">
              <Smartphone className="h-4 w-4" />
              <span className="hidden sm:inline">SMS</span>
            </div>
          </div>

          {/* Notification Type Rows */}
          <div className="space-y-4">
            {NOTIFICATION_TYPES.map((config, index) => (
              <React.Fragment key={config.type}>
                <div className="grid grid-cols-[1fr,auto,auto,auto] gap-4 items-center">
                  <div>
                    <Label className="font-medium">{config.label}</Label>
                    <p className="text-sm text-muted-foreground">{config.description}</p>
                  </div>
                  <div className="flex justify-center min-w-[60px]">
                    <Switch
                      checked={preferences[config.type]?.email_enabled ?? true}
                      onCheckedChange={(value) => handleToggle(config.type, "email_enabled", value)}
                    />
                  </div>
                  <div className="flex justify-center min-w-[60px]">
                    <Switch
                      checked={preferences[config.type]?.in_app_enabled ?? true}
                      onCheckedChange={(value) => handleToggle(config.type, "in_app_enabled", value)}
                    />
                  </div>
                  <div className="flex justify-center min-w-[60px]">
                    <Switch
                      checked={preferences[config.type]?.sms_enabled ?? false}
                      onCheckedChange={(value) => handleToggle(config.type, "sms_enabled", value)}
                      disabled // SMS not implemented yet
                    />
                  </div>
                </div>
                {index < NOTIFICATION_TYPES.length - 1 && <Separator />}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-6 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> SMS notifications are coming soon. Your preferences will be saved for when this feature launches.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationSettings;
